import { AdminUser, Permiso } from '../models/api.models';

/**
 * Permission codes used in the system
 */
export type PermissionCode = 'get_users' | 'manage_users' | 'manage_rooms' | 'manage_guests' | 'manage_bookings' | 'manage_inventory' | 'manage_complaints';

/**
 * Check if a user has a specific permission
 */
export function hasPermission(user: AdminUser | null, permissionCode: PermissionCode): boolean {
    if (!user || !user.permisos) {
        return false;
    }
    return user.permisos.some(p => p.codigo === permissionCode);
}

/**
 * Check if user can view users (requires get_users permission)
 */
export function canViewUsers(user: AdminUser | null): boolean {
    return hasPermission(user, 'get_users');
}

/**
 * Check if user can manage users (requires manage_users permission)
 */
export function canManageUsers(user: AdminUser | null): boolean {
    return hasPermission(user, 'manage_users');
}

/**
 * Check if user can manage rooms (requires manage_rooms permission)
 */
export function canManageRooms(user: AdminUser | null): boolean {
    return hasPermission(user, 'manage_rooms');
}

/**
 * Check if user can manage guests (requires manage_guests permission)
 */
export function canManageGuests(user: AdminUser | null): boolean {
    return hasPermission(user, 'manage_guests');
}

/**
 * Check if user can manage bookings (requires manage_bookings permission)
 */
export function canManageBookings(user: AdminUser | null): boolean {
    return hasPermission(user, 'manage_bookings');
}

/**
 * Check if user can manage complaints (requires manage_complaints permission)
 */
export function canManageComplaints(user: AdminUser | null): boolean {
    return hasPermission(user, 'manage_complaints');
}

/**
 * Check if a user is the super admin (user ID 1)
 */
export function isSuperAdmin(userId: number | null | undefined): boolean {
    return userId === 1;
}

/**
 * Get permission names as a comma-separated string
 */
export function getPermissionNames(permisos: Permiso[]): string {
    if (!permisos || permisos.length === 0) {
        return 'Sin permisos';
    }
    return permisos.map(p => p.nombre).join(', ');
}
